import { Button, Popover, PopoverProps } from 'antd';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIONS, Joyride, Step, TooltipRenderProps } from 'react-joyride';
import { discoveryPanelID } from './DiscoveryPanel';
import { musicSheetContainerID, musicSheetPaginationClass } from './MelodyAnalysis';
import { melodyGraphID } from './MelodyGraph';
import { melodicPatternConfigurationID } from './PatternConfiguration';
import { suggestionsID } from './Suggestions';
import { timelineID } from './Timeline';

/** The analysis tabs a step of the tour can need. */
export type TourTab = 'discovery' | 'operators';

const nextFrames = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );

// Content and title are translation keys, the tooltip translates them.
const tourSteps = (showTab: (tab: TourTab) => void): Step[] => {
  // The steps of the analysis column first open their tab, whose content is laid out before the step looks for it.
  const inTab = (tab: TourTab) => async () => {
    showTab(tab);
    await nextFrames();
  };

  return [
    {
      content: 'onBoarding.step1.description',
      placement: 'right',
      skipBeacon: true,
      target: `#${musicSheetContainerID}`,
      title: 'onBoarding.step1.title'
    },
    {
      content: 'onBoarding.step2.description',
      placement: 'bottom',
      skipBeacon: true,
      target: musicSheetPaginationClass,
      title: 'onBoarding.step2.title'
    },
    {
      content: 'onBoarding.step3.description',
      placement: 'bottom',
      skipBeacon: true,
      target: `#${timelineID}`,
      title: 'onBoarding.step3.title'
    },
    {
      before: inTab('discovery'),
      content: 'onBoarding.discovery.description',
      placement: 'left',
      skipBeacon: true,
      target: `#${discoveryPanelID}`,
      title: 'onBoarding.discovery.title'
    },
    {
      before: inTab('operators'),
      content: 'onBoarding.step4.description',
      placement: 'left',
      skipBeacon: true,
      target: `#${suggestionsID}`,
      title: 'onBoarding.step4.title'
    },
    {
      before: inTab('operators'),
      content: 'onBoarding.step5.description',
      placement: 'left',
      skipBeacon: true,
      target: `#${melodyGraphID}`,
      title: 'onBoarding.step5.title'
    },
    {
      before: inTab('operators'),
      content: 'onBoarding.step6.description',
      placement: 'top',
      skipBeacon: true,
      target: `#${melodicPatternConfigurationID}`,
      title: 'onBoarding.step6.title'
    }
  ];
};

const Tooltip = ({
  continuous,
  index,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps
}: TooltipRenderProps) => {
  const { t: translate } = useTranslation();

  return (
    <div>
      <Popover
        {...tooltipProps}
        content={
          <>
            {translate(step.content as string)}
            <div className="mt-4 flex items-center justify-between gap-4">
              <Button {...skipProps} size="small" type="link">
                {translate('onBoarding.tooltip.skip')}
              </Button>
              <span
                className="text-xs text-slate-600"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {translate('onBoarding.tooltip.progress', { current: index + 1, total: size })}
              </span>
              <div>
                <Button {...backProps} className="mr-4" disabled={index === 0} size="small">
                  {translate('onBoarding.tooltip.back')}
                </Button>
                {continuous && index < size - 1 ? (
                  <Button {...primaryProps} size="small" type="primary">
                    {translate('onBoarding.tooltip.next')}
                  </Button>
                ) : (
                  <Button {...closeProps} size="small" type="primary">
                    {translate('onBoarding.tooltip.close')}
                  </Button>
                )}
              </div>
            </div>
          </>
        }
        open={true}
        placement={step.placement as PopoverProps['placement']}
        // Tailwind's max-w-sm (24rem), antd 6 sets its own max-width on the root.
        styles={{ root: { maxWidth: '24rem' } }}
        title={translate(step.title as string)}
      ></Popover>
    </div>
  );
};

const OnBoarding = ({
  isVisible,
  setIsVisible,
  showTab
}: {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  showTab: (tab: TourTab) => void;
}) => {
  const steps = useMemo(() => tourSteps(showTab), [showTab]);

  return isVisible ? (
    <Joyride
      continuous
      onEvent={(event) =>
        (event.action === ACTIONS.CLOSE || event.action === ACTIONS.SKIP) && setIsVisible(false)
      }
      // As in react-joyride 2, the highlighted element does not take clicks during the tour.
      options={{ blockTargetInteraction: true }}
      run
      steps={steps}
      tooltipComponent={Tooltip}
    />
  ) : null;
};

export default OnBoarding;
